/**
 * WarehousesSettingsView — ניהול מחסנים
 *
 * Phase 11: Full CRUD interface for the  inventory_warehouses  table.
 *
 * Columns managed:
 *   warehouse_number  — text, optional label / code
 *   name              — text, required
 *   is_consignment    — boolean (מחסן קונסיגנציה)
 */

import { useEffect, useState, useCallback } from 'react';
import { Warehouse, Plus, Pencil, Trash2, X, Loader as Loader2, CircleAlert as AlertCircle, Hash, Tag, PackageCheck, PackageX, ToggleLeft, ToggleRight, Search } from 'lucide-react';
import { supabase } from '@/lib/supabase';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface InventoryWarehouse {
  id:               string;
  company_id:       string;
  warehouse_number: string;
  name:             string;
  is_consignment:   boolean;
  is_external:      boolean;
  external_source:  string | null;
  created_at:       string;
  updated_at:       string;
}

interface WarehouseForm {
  warehouse_number: string;
  name:             string;
  is_consignment:   boolean;
  is_external:      boolean;
  external_source:  string;
}

const EMPTY_FORM: WarehouseForm = {
  warehouse_number: '',
  name:             '',
  is_consignment:   false,
  is_external:      false,
  external_source:  '',
};

const EXTERNAL_SOURCE_OPTIONS = [
  { value: 'shopify', label: 'Shopify' },
];

interface WarehousesSettingsViewProps {
  companyId:                  string | null;
  showToast:                  (msg: string, ok?: boolean) => void;
  companyConsignmentEnabled?: boolean;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function WarehousesSettingsView({ companyId, showToast, companyConsignmentEnabled = false }: WarehousesSettingsViewProps) {

  // ── State ──────────────────────────────────────────────────────────────────
  const [warehouses, setWarehouses] = useState<InventoryWarehouse[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState<string | null>(null);

  const [modalOpen,  setModalOpen]  = useState(false);
  const [editing,    setEditing]    = useState<InventoryWarehouse | null>(null);
  const [form,       setForm]       = useState<WarehouseForm>(EMPTY_FORM);
  const [formError,  setFormError]  = useState<string | null>(null);
  const [saving,     setSaving]     = useState(false);

  const [delTarget,  setDelTarget]  = useState<InventoryWarehouse | null>(null);
  const [deleting,   setDeleting]   = useState(false);

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchWarehouses = useCallback(async () => {
    if (!companyId) return;
    setLoading(true); setError(null);
    const { data, error: err } = await supabase
      .from('inventory_warehouses')
      .select('id, company_id, warehouse_number, name, is_consignment, is_external, external_source, created_at, updated_at')
      .eq('company_id', companyId)
      .order('name');
    if (err) {
      console.error('[WarehousesSettingsView] SELECT error:', err);
      setError(`שגיאה בטעינת מחסנים: ${err.message} (קוד: ${err.code})`);
    } else {
      setWarehouses((data as InventoryWarehouse[]) ?? []);
    }
    setLoading(false);
  }, [companyId]);

  useEffect(() => { fetchWarehouses(); }, [fetchWarehouses]);

  // ── Modal helpers ──────────────────────────────────────────────────────────
  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormError(null);
    setModalOpen(true);
  };

  const openEdit = (w: InventoryWarehouse) => {
    setEditing(w);
    setForm({
      warehouse_number: w.warehouse_number,
      name:             w.name,
      is_consignment:   w.is_consignment,
      is_external:      w.is_external,
      external_source:  w.external_source ?? '',
    });
    setFormError(null);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormError(null);
  };

  const setField = <K extends keyof WarehouseForm>(k: K, v: WarehouseForm[K]) =>
    setForm(prev => ({ ...prev, [k]: v }));

  // ── Validate ───────────────────────────────────────────────────────────────
  const validate = (): string | null => {
    if (!form.name.trim()) return 'שם המחסן הוא שדה חובה';
    if (form.is_external && !form.external_source) return 'יש לבחור מערכת חיצונית';
    return null;
  };

  // ── Save ───────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!companyId) { setFormError('לא נבחרה חברה פעילה — אנא רענן את הדף'); return; }
    const err = validate();
    if (err) { setFormError(err); return; }
    setSaving(true); setFormError(null);

    const payload = {
      warehouse_number: form.warehouse_number.trim(),
      name:             form.name.trim(),
      is_consignment:   form.is_external ? false : form.is_consignment,
      is_external:      form.is_external,
      external_source:  form.is_external ? form.external_source : null,
    };

    try {
      if (editing) {
        const { error: e } = await supabase
          .from('inventory_warehouses')
          .update(payload)
          .eq('id', editing.id);
        if (e) throw e;
        showToast('מחסן עודכן בהצלחה');
      } else {
        const { error: e } = await supabase
          .from('inventory_warehouses')
          .insert({ company_id: companyId, ...payload }); // companyId is non-null (guarded above)
        if (e) throw e;
        showToast('מחסן נוסף בהצלחה');
      }
      closeModal();
      fetchWarehouses();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'שגיאה בשמירה');
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ─────────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!delTarget) return;
    setDeleting(true);
    const { error: e } = await supabase
      .from('inventory_warehouses')
      .delete()
      .eq('id', delTarget.id);
    if (e) { showToast(e.message, false); }
    else   {
      showToast('מחסן נמחק');
      setDelTarget(null);
      fetchWarehouses();
    }
    setDeleting(false);
  };

  const filteredWarehouses = warehouses.filter(w => {
    if (!searchTerm.trim()) return true;
    const q = searchTerm.trim().toLowerCase();
    return (w.name?.toLowerCase().includes(q)) ||
           (w.warehouse_number?.toLowerCase().includes(q));
  });

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <section className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <Warehouse className="w-5 h-5 text-sky-600" />
            <div>
              <h2 className="text-base font-semibold text-gray-900">ניהול מחסנים</h2>
              <p className="text-xs text-gray-500">הגדר מחסנים רגילים ומחסני קונסיגנציה</p>
            </div>
          </div>
          <button
            onClick={openAdd}
            disabled={!companyId}
            className="flex items-center gap-2 px-4 py-2 bg-sky-600 hover:bg-sky-700
                       text-white text-sm font-medium rounded-lg transition-colors
                       disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-4 h-4" />
            הוסף מחסן
          </button>
        </div>

        {!loading && warehouses.length > 0 && (
          <div className="px-6 py-3 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="חיפוש לפי שם או מספר מחסן..."
                className="w-full pr-10 pl-4 py-2 text-sm border border-gray-200 rounded-lg
                           focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-400
                           placeholder:text-gray-400 transition-colors"
              />
              {searchTerm && (
                <button onClick={() => setSearchTerm('')}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Error banner */}
        {error && (
          <div className="flex items-center gap-3 mx-6 my-4 px-4 py-3 bg-red-50
                          border border-red-200 rounded-xl text-sm text-red-700">
            <AlertCircle className="w-4 h-4 shrink-0" />{error}
          </div>
        )}

        {/* Loading */}
        {loading ? (
          <div className="flex items-center justify-center py-14 gap-3 text-gray-400">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="text-sm">טוען מחסנים…</span>
          </div>
        ) : warehouses.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3 text-gray-400">
            <Warehouse className="w-10 h-10 text-gray-200" />
            <p className="text-sm font-medium">אין מחסנים עדיין</p>
            <p className="text-xs text-gray-400">לחץ על "הוסף מחסן" כדי להוסיף את המחסן הראשון</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100 text-xs font-semibold
                               text-gray-500 uppercase tracking-wide">
                  <th className="px-6 py-3 text-right">מחסן</th>
                  <th className="px-6 py-3 text-right">מס׳ מחסן</th>
                  <th className="px-6 py-3 text-right">סוג</th>
                  <th className="px-6 py-3 text-right w-28">פעולות</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filteredWarehouses.map(w => (
                  <tr key={w.id} className="hover:bg-gray-50/60 transition-colors">

                    {/* Name + icon */}
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2.5">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center
                                         shrink-0 text-xs font-bold
                                         ${w.is_consignment
                                           ? 'bg-amber-100 text-amber-700'
                                           : 'bg-sky-100 text-sky-700'}`}>
                          <Warehouse className="w-4 h-4" />
                        </div>
                        <span className="font-medium text-gray-900">{w.name}</span>
                      </div>
                    </td>

                    {/* Warehouse number */}
                    <td className="px-6 py-4 text-gray-500 text-xs">
                      {w.warehouse_number ? (
                        <span className="flex items-center gap-1">
                          <Hash className="w-3 h-3 text-gray-400" />
                          {w.warehouse_number}
                        </span>
                      ) : <span className="text-gray-300">—</span>}
                    </td>

                    {/* Type badge */}
                    <td className="px-6 py-4">
                      {w.is_external ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5
                                         bg-purple-50 text-purple-700 text-xs font-semibold rounded-full
                                         border border-purple-200">
                          <PackageCheck className="w-3 h-3" />
                          חיצוני ({w.external_source ?? 'API'})
                        </span>
                      ) : w.is_consignment ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5
                                         bg-amber-50 text-amber-700 text-xs font-semibold rounded-full
                                         border border-amber-200">
                          <PackageCheck className="w-3 h-3" />
                          קונסיגנציה
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5
                                         bg-sky-50 text-sky-700 text-xs font-semibold rounded-full
                                         border border-sky-200">
                          <PackageX className="w-3 h-3" />
                          רגיל
                        </span>
                      )}
                    </td>

                    {/* Actions */}
                    <td className="px-6 py-4">
                      {w.is_external ? (
                        <span className="text-xs text-gray-400">מנוהל אוטומטית</span>
                      ) : (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => openEdit(w)}
                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50
                                       rounded-lg transition-colors"
                            title="ערוך"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setDelTarget(w)}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50
                                       rounded-lg transition-colors"
                            title="מחק"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {warehouses.length > 0 && (
          <div className="px-6 py-3 border-t border-gray-50 text-xs text-gray-400 flex items-center gap-3">
            <span>{warehouses.length} מחסנים</span>
            <span className="text-gray-200">·</span>
            <span className="inline-flex items-center gap-1">
              <PackageCheck className="w-3 h-3 text-amber-500" />
              {warehouses.filter(w => w.is_consignment).length} קונסיגנציה
            </span>
            <span className="inline-flex items-center gap-1">
              <PackageX className="w-3 h-3 text-sky-500" />
              {warehouses.filter(w => !w.is_consignment).length} רגיל
            </span>
          </div>
        )}
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          Add / Edit Modal
          ══════════════════════════════════════════════════════════════════════ */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center
                        bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6
                          space-y-5 max-h-[90vh] overflow-y-auto">

            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900">
                {editing ? 'עריכת מחסן' : 'הוספת מחסן חדש'}
              </h3>
              <button
                onClick={closeModal}
                className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {formError && (
              <div className="flex items-center gap-2 px-3 py-2.5 bg-red-50
                              border border-red-200 rounded-lg text-sm text-red-700">
                <AlertCircle className="w-4 h-4 shrink-0" />{formError}
              </div>
            )}

            {/* Name */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                <Tag className="w-3.5 h-3.5 text-sky-500" />
                שם המחסן <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.name}
                onChange={e => setField('name', e.target.value)}
                placeholder="לדוגמה: מחסן ראשי תל-אביב"
                autoFocus
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm
                           focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent"
              />
            </div>

            {/* Warehouse number */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                <Hash className="w-3.5 h-3.5 text-gray-400" />
                מספר / קוד מחסן
                <span className="text-xs font-normal text-gray-400">(אופציונלי)</span>
              </label>
              <input
                type="text"
                value={form.warehouse_number}
                onChange={e => setField('warehouse_number', e.target.value)}
                placeholder="לדוגמה: WH-01"
                dir="ltr"
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm
                           focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent"
              />
            </div>

            {/* External warehouse toggle */}
            <div className={`flex items-center justify-between px-4 py-3.5 rounded-xl border
                             transition-colors cursor-pointer
                             ${form.is_external
                               ? 'bg-purple-50 border-purple-300'
                               : 'bg-gray-50 border-gray-200 hover:border-gray-300'}`}
                 onClick={() => {
                   const next = !form.is_external;
                   setForm(prev => ({
                     ...prev,
                     is_external: next,
                     is_consignment: next ? false : prev.is_consignment,
                     external_source: next ? (prev.external_source || 'shopify') : '',
                   }));
                 }}
                 role="checkbox"
                 aria-checked={form.is_external}
                 tabIndex={0}
                 onKeyDown={e => {
                   if (e.key === ' ') {
                     const next = !form.is_external;
                     setForm(prev => ({
                       ...prev,
                       is_external: next,
                       is_consignment: next ? false : prev.is_consignment,
                       external_source: next ? (prev.external_source || 'shopify') : '',
                     }));
                   }
                 }}
            >
              <div>
                <p className={`text-sm font-semibold ${form.is_external ? 'text-purple-800' : 'text-gray-700'}`}>
                  מחסן חיצוני (API)
                </p>
                <p className="text-xs text-gray-400 mt-0.5">
                  מלאי המתקבל ממערכת חיצונית באמצעות סנכרון
                </p>
              </div>
              {form.is_external
                ? <ToggleRight className="w-8 h-8 text-purple-500 shrink-0" />
                : <ToggleLeft  className="w-8 h-8 text-gray-300 shrink-0" />
              }
            </div>

            {/* External source selector + warning */}
            {form.is_external && (
              <>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-gray-700">מערכת חיצונית</label>
                  <select
                    value={form.external_source}
                    onChange={e => setField('external_source', e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm
                               focus:outline-none focus:ring-2 focus:ring-purple-400
                               focus:border-transparent bg-white"
                  >
                    <option value="">-- בחר מערכת --</option>
                    {EXTERNAL_SOURCE_OPTIONS.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-start gap-2 px-3 py-2.5 bg-amber-50
                                border border-amber-200 rounded-lg text-xs text-amber-800">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-amber-500" />
                  <span>
                    <strong>שימו לב:</strong> יש לוודא חיבור API תואם בממשק החברה
                    (הגדרות &rarr; אינטגרציות) לפני הפעלת סנכרון מלאי חיצוני.
                  </span>
                </div>
              </>
            )}

            {/* Consignment toggle — hidden when external */}
            {form.is_external ? null : !companyConsignmentEnabled ? (
              <div className="flex items-start gap-2 px-4 py-3 bg-gray-100 border border-gray-300 rounded-xl text-sm text-gray-600">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-gray-400" />
                <span>
                  מכירה בקונסגנציה לא מופעלת לחברה זו.
                  כדי לפתוח מחסן קונסגנציה, יש להפעיל תחילה את{' '}
                  <strong>הגדרות חברה → הגדרות קונסגנציה</strong>.
                </span>
              </div>
            ) : (
              <>
                <div className={`flex items-center justify-between px-4 py-3.5 rounded-xl border
                                 transition-colors cursor-pointer
                                 ${form.is_consignment
                                   ? 'bg-amber-50 border-amber-300'
                                   : 'bg-gray-50 border-gray-200 hover:border-gray-300'}`}
                     onClick={() => setField('is_consignment', !form.is_consignment)}
                     role="checkbox"
                     aria-checked={form.is_consignment}
                     tabIndex={0}
                     onKeyDown={e => e.key === ' ' && setField('is_consignment', !form.is_consignment)}
                >
                  <div>
                    <p className={`text-sm font-semibold ${form.is_consignment ? 'text-amber-800' : 'text-gray-700'}`}>
                      מחסן קונסיגנציה
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      סחורה שנמצאת אצל הלקוח אך עדיין בבעלות החברה
                    </p>
                  </div>
                  {form.is_consignment
                    ? <ToggleRight className="w-8 h-8 text-amber-500 shrink-0" />
                    : <ToggleLeft  className="w-8 h-8 text-gray-300 shrink-0" />
                  }
                </div>

                {/* Info banner when consignment is on */}
                {form.is_consignment && (
                  <div className="flex items-start gap-2 px-3 py-2.5 bg-amber-50
                                  border border-amber-200 rounded-lg text-xs text-amber-700">
                    <PackageCheck className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    מחסן זה יהיה זמין לשיוך ללקוחות קונסיגנציה.
                  </div>
                )}
              </>
            )}

            <div className="flex items-center gap-3 justify-end pt-1">
              <button
                onClick={closeModal}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800
                           border border-gray-200 rounded-lg transition-colors"
              >
                ביטול
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !form.name.trim()}
                className="flex items-center gap-2 px-5 py-2 bg-sky-600 hover:bg-sky-700
                           disabled:opacity-50 disabled:cursor-not-allowed
                           text-white text-sm font-medium rounded-lg transition-colors"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {editing ? 'שמור שינויים' : 'הוסף מחסן'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          Delete Confirm Modal
          ══════════════════════════════════════════════════════════════════════ */}
      {delTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center
                        bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6 space-y-5">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-red-100 rounded-xl shrink-0">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900">מחיקת מחסן</h3>
                <p className="text-sm text-gray-500 mt-1">
                  האם למחוק את המחסן{' '}
                  <span className="font-semibold text-gray-800">"{delTarget.name}"</span>?
                  {delTarget.is_consignment && (
                    <span className="block mt-1 text-amber-600">
                      לקוחות המשויכים למחסן קונסיגנציה זה ישוחררו (השיוך יאופס).
                    </span>
                  )}
                  {' '}פעולה זו אינה ניתנת לביטול.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 justify-end">
              <button
                onClick={() => setDelTarget(null)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800
                           border border-gray-200 rounded-lg transition-colors"
              >
                ביטול
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex items-center gap-2 px-5 py-2 bg-red-600 hover:bg-red-700
                           disabled:opacity-50 text-white text-sm font-medium
                           rounded-lg transition-colors"
              >
                {deleting && <Loader2 className="w-4 h-4 animate-spin" />}
                מחק
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
